
import BackupDetailsDialog from '@/components/dialogs/backups/BackupDetailsDialog.vue';
import DeleteBackupDialog from '@/components/dialogs/backups/DeleteBackupDialog.vue';
import RestoreBackupDialog from '@/components/dialogs/backups/RestoreBackupDialog.vue';
import {
BackupDto,
BackupId,
BackupRepositoryInterface,
BackupSummaryDto,
RestoreBackupDto
} from '@/services/Backup/interfaces/index';
import { AxiosError } from 'axios';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import { Website } from "@/services/Website/interfaces";
import { ServerInterface, ServerRepositoryInterface, ServerType } from "@/services/Server/Interfaces";
import PageHeader from '@/components/layout/PageHeader.vue';
import {
  LIST_FOOTER_PROPS,
  ListPaginationOptions,
  buildListPaginationQuery,
  defaultListOptions,
  effectivePageSize,
  listPaginationQueryEquals,
  parseListQuery,
} from '@/utils/listPagination';

@Component({
  components: {
    BackupDetailsDialog,
    RestoreBackupDialog,
    DeleteBackupDialog,
    PageHeader,
  },
  inject: [
    'backupRepository',
    'serverRepository'
  ],
})
export default class AllBackup extends Vue {
  public loading: boolean = false;
  public options: ListPaginationOptions = defaultListOptions();
  public totalItems: number = 0;
  public LIST_FOOTER_PROPS = LIST_FOOTER_PROPS;

  public backupRepository: BackupRepositoryInterface;
  public serverRepository: ServerRepositoryInterface;

  public backups: BackupSummaryDto[] = [];

  public headers: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Repository', value: 'repositoryId' },
    { text: 'Origin Server', value: 'repo.targetOriginServerId' },
    { text: 'Scope', value: 'scope' },
    { text: 'Website Group', value: 'task.websiteGroupId' },
    { text: 'Status', value: 'status' },
    { text: 'Created At', value: 'createdAt' },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  public scopeLabels = {
    'account': 'Terminated Backup',
    'server': 'Server Backup',
  };

  public searchOptions = {};

  public deleteDialog: string = '';
  public deleteDialogData: BackupDto | null = null;

  private _searchServerId: string = '';
  private servers: ServerInterface[] = [];
  private ignoreRouteSync = false;
  private applyingRouteQuery = false;

  public applyRouteQuery(): void {
    this.applyingRouteQuery = true;
    try {
      this.options = parseListQuery(this.$route.query);
      this.$nextTick(() => {
        this.applyingRouteQuery = false;
      });
    } catch (err) {
      this.applyingRouteQuery = false;
      throw err;
    }
  }

  public async syncRouteQuery(): Promise<void> {
    if (this.ignoreRouteSync || this.applyingRouteQuery) {
      return;
    }

    const query = buildListPaginationQuery(this.options);
    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }

    this.ignoreRouteSync = true;
    try {
      await this.$router.replace({ name: 'backup_list', query });
    } catch (err) {
      // Ignore NavigationDuplicated.
    } finally {
      this.$nextTick(() => {
        this.ignoreRouteSync = false;
      });
    }
  }

  public async initialize(): Promise<void> {
    await  this.serverRepository.getServersOfType(ServerType.ORIGIN).then((servers) => {
      this.servers = servers;
    }).catch((err) => {
      this.$errorDialog.show(err.message);
    });

    await this.search();
  }

  public async created(): Promise<void> {
    this.applyRouteQuery();
    await this.initialize();
  }

  public async search(): Promise<void> {
    this.loading = true;
    try {
      await this.getBackups();
    } catch (err) {
     await this.onError(err);
    } finally {
      this.loading = false;
    }
  }

  get searchServerId(): string {
    return this._searchServerId;
  }

  set searchServerId(value: string) {
    this._searchServerId = value;
    this.options.page = 1;
    this.syncRouteQuery();
    this.search();
  }

  public async getBackups(): Promise<void> {
    const result = await this.backupRepository.searchBackups(
      (this._searchServerId ?? '') !== '' ? this._searchServerId : undefined,
      {
      page: this.options.page,
      pageSize: effectivePageSize(this.options.itemsPerPage),
    });

    this.totalItems = result?.total ?? 0;
    this.backups = result?.data ?? [];
  }

  public async onPageChange(): Promise<void> {
    await this.syncRouteQuery();
    await this.search();
  }

  public async onItemsPerPageChange(): Promise<void> {
    this.options.page = 1;
    await this.syncRouteQuery();
    await this.search();
  }

  @Watch('$route')
  public async onRouteChange(): Promise<void> {
    if (this.ignoreRouteSync) {
      return;
    }
    if (this.$route.name !== 'backup_list') {
      return;
    }
    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }

    this.applyRouteQuery();
    await this.search();
  }

  public async goToDetails(backupId: BackupId): Promise<void> {
    await this.$router.push({ name: 'backup_details', params: { backupId } });
  }

  public async showDeleteDialogFor(id: BackupId): Promise<void> {
    try {
      const backup = await this.backupRepository.getBackup(id);
      if (!backup) {
        return;
      }

      this.deleteDialog = 'delete-backup-dialog';
      this.deleteDialogData = backup;
    } catch (err) {
      await this.onError(err);
    }
  }

  public async goToCreateBackup(): Promise<void> {
    await this.$router.push({ name: 'task_create' });
  }

  public async deleteBackup(backup: BackupDto): Promise<void> {
    try {
      await this.backupRepository.deleteBackup(backup.id);

      this.$successDialog.show('Backup deleted successfully');

      await this.search();
    } catch (err) {
      await this.onError(err);
    }
  }

  private async onError(err: unknown): Promise<void> {
    const asAxiosError = err as AxiosError;
    if (asAxiosError.isAxiosError) {
      this.$errorDialog.show(asAxiosError.response?.data?.message ?? 'An axios error occurred');
    } else if (err instanceof Error) {
      this.$errorDialog.show(err.message);
    } else {
      this.$errorDialog.show('An unknown error occurred');
    }
  }
}
